import React, { useEffect, useRef } from 'react';
import { fetchAll } from './store/slices/geo-location.slice';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { GdprService } from '@/hotdesk-ui/common-classes/gdpr.service';
import { getCurrency, getDefaultCurrency } from '@/hotdesk-ui/store/slices/currency.slice';
import { fetchFavorites, clearFavorites } from '@/hotdesk-ui/store/slices/favorites.slice';
import { fetchListingTypes } from '@/hotdesk-ui/store/slices/listing-types.slice';
import { fetchWorkspaceCategories } from '@/hotdesk-ui/store/slices/workspace-categories.slice';
import { fetchAmenities } from '@/hotdesk-ui/store/slices/amenities.slice';
import { usePathname } from 'next/navigation';
import { useLocale } from 'next-intl';
import { RootState } from '@/hotdesk-ui/store/store';
import { restoreItidaCustomSignal, initialFetchRemoteConfig } from './common-classes/firebase.service';
import { tracking } from '@/hotdesk-ui/services/tracking.service';
import { fetchSubscriptionCoverage } from '@/hotdesk-ui/store/slices/listing.slice';
const AppInitializer: React.FC = () => {
  const dispatch = useAppDispatch();
  const gdpr = new GdprService();
  const pathname = usePathname();
  const locale = useLocale();
  const defaultCurrency = useAppSelector((state: RootState) => state.currency.defaultCurrency);
  const currencyUserSelected = useAppSelector((state: RootState) => state.currency.currencyUserSelected);
  const currencyRehydrated = useAppSelector((state: RootState) => !!(state.currency as {
    _persist?: {
      rehydrated?: boolean;
    };
  })._persist?.rehydrated);
  const currencies = useAppSelector((state: RootState) => state.currency.currency);
  const user = useAppSelector((state: RootState) => state.auth.user);
  const token = useAppSelector((state: RootState) => state.auth.token);
  const geo = useAppSelector((state: RootState) => state.geoLocation.geoLocationZipData);
  useEffect(() => {
    dispatch(fetchAll());
    const webviewRoutes = ['/request-visit', '/feedback', '/delete-account', '/long-term-quote', '/business-form'];
    const isWebviewRoute = webviewRoutes.some(route => new RegExp(`^${route.replace(/:[^\s/]+/g, '([^/]+)')}$`, 'u').test(pathname));
    if (!isWebviewRoute) {
      gdpr.configureGDPR();
    }
    ;
    // Load exchange rates on every visit. The persisted list only bridges the
    // moment until this lands; reusing it instead froze each browser's rates
    // at its first visit (e.g. EGP at ~28/USD long after it passed 50).
    dispatch(getCurrency());
    dispatch(fetchWorkspaceCategories());
    dispatch(fetchAmenities());
  }, []);

  // Refetch listing types whenever the locale changes so the navbar/hero
  // dropdowns stay in sync with the current language. The filter modal
  // already gets translated types via the server prop, but redux-driven
  // consumers (HomeHero, HeroPills, navbar search) read from this slice.
  useEffect(() => {
    dispatch(fetchListingTypes(locale));
  }, [locale, dispatch]);

  // Re-detect the default currency once per page load unless the visitor
  // picked one. Wait for rehydration so a saved pick is known before deciding.
  const currencyDetectedRef = useRef(false);
  useEffect(() => {
    if (currencyDetectedRef.current) return;
    if (!currencies.length || !currencyRehydrated) return;
    currencyDetectedRef.current = true;
    if (!currencyUserSelected) {
      dispatch(getDefaultCurrency({
        currencies
      }));
    }
  }, [currencies, currencyRehydrated, currencyUserSelected, dispatch]);

  // Fetch favorites when user logs in, clear on logout
  useEffect(() => {
    if (user?.id) {
      dispatch(fetchFavorites(user.id));
    } else {
      dispatch(clearFavorites());
    }
  }, [user?.id]);

  // Resolve the subscription's promo-covered workspace/listing ids near the
  // current location so cards can badge "Applied" consistently on every view.
  useEffect(() => {
    if (user?.id && geo?.lat && geo?.log) {
      dispatch(fetchSubscriptionCoverage({
        lastSearch: geo
      }));
    }
  }, [user?.id, geo?.lat, geo?.log, dispatch]);
  const prevAuthUserIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (user?.id == null) {
      if (prevAuthUserIdRef.current != null) {
        prevAuthUserIdRef.current = undefined;
        tracking.clearAuthenticatedUser();
      }
      return;
    }
    prevAuthUserIdRef.current = user.id;
    if (!token) return;
    let cancelled = false;
    void tracking.syncAuthenticatedUserProfile(user, token, locale, geo).catch(() => {
      // Non-fatal: profile sync failed; events may still use partial user map.
    }).finally(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- geo fields only; full `geo` object is unstable
  }, [user, token, locale, geo?.city, geo?.country, geo?.lat, geo?.log]);
  useEffect(() => {
    if (defaultCurrency?.code) {
      tracking.addUserVariable('currency', defaultCurrency.code);
    }
  }, [defaultCurrency?.code]);
  useEffect(() => {
    const initApp = async () => {
      await restoreItidaCustomSignal();
      await initialFetchRemoteConfig();
      await tracking.init();
    };
    initApp();
  }, []);
  return null; // This component doesn't render anything
};
export default AppInitializer;